import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Users, Check, Bell, ArrowRight, Sparkles, Mail, AlertCircle } from 'lucide-react'
import api from '../services/api'
import { T } from '../theme'
import { formatSeasonLong } from '../context/SeasonContext'

/**
 * SeasonClubOverview — vue de préparation saison pour un DS pur (plan CLUB sans catégorie).
 *
 * Affiche la liste des coachs du club avec leur statut de préparation pour la saison cible.
 * Le DS peut envoyer une notification à chaque coach non préparé pour lui rappeler.
 *
 * Différent de SeasonAssistant qui est destiné aux coachs individuels (1 utilisateur = 1 équipe).
 */

export default function SeasonClubOverview({ targetSeason, isMobile = false }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notifyingId, setNotifyingId] = useState(null)
  const [notifiedIds, setNotifiedIds] = useState(new Set())

  useEffect(() => {
    if (!targetSeason) return
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const { data } = await api.get('/coach/club-preparation-status/', { params: { season: targetSeason } })
        if (!cancelled) setData(data)
      } catch (e) {
        if (!cancelled) setError(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Impossible de charger la liste des coachs.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [targetSeason])

  const handleNotify = async (userId) => {
    if (!userId) return
    setNotifyingId(userId)
    try {
      await api.post('/coach/notify-prepare-season/', { user_id: userId, season: targetSeason })
      setNotifiedIds(prev => new Set([...prev, userId]))
    } catch (e) {
      alert(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Erreur lors de l\'envoi de la notification.')
    } finally {
      setNotifyingId(null)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ width: 28, height: 28, border: `2px solid ${T.goldBdr}`, borderTopColor: T.gold, borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 14px' }} />
        <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted }}>Chargement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: T.red }}>
        <AlertCircle size={32} style={{ margin: '0 auto 12px', display: 'block' }} />
        <p style={{ fontSize: 14 }}>{error}</p>
      </div>
    )
  }

  if (!data) return null

  const { coaches = [], total = 0, prepared_count = 0, club_name } = data
  const remaining = total - prepared_count
  const allPrepared = total > 0 && remaining === 0

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      {/* ── HEADER DARK ── */}
      <div style={{
        background: T.dark, padding: isMobile ? '28px 20px' : '40px 36px',
        marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
          backgroundImage: 'linear-gradient(rgba(79,142,247,1) 1px, transparent 1px), linear-gradient(90deg, rgba(79,142,247,1) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }} />

        <div style={{ position: 'relative' }}>
          <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(79,142,247,0.7)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 14, height: 1, background: T.gold, display: 'inline-block' }} />
            Préparation · {formatSeasonLong(targetSeason)}
          </p>

          <h1 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 42, textTransform: 'uppercase', lineHeight: .95, letterSpacing: '.01em', color: '#f5f2eb', marginBottom: 10 }}>
            {club_name}<br />
            <span style={{ color: T.gold }}>nouvelle saison.</span>
          </h1>

          <p style={{ fontSize: isMobile ? 13 : 14, color: 'rgba(245,242,235,0.6)', lineHeight: 1.6, marginTop: 16, maxWidth: 620 }}>
            En tant que directeur sportif, suis ici l'avancement de la préparation de chaque coach pour la saison {targetSeason}. Chaque coach gère son propre effectif et sa catégorie.
          </p>
        </div>
      </div>

      {/* ── KPI BAR ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 1, background: T.rule, marginBottom: 24 }}>
        <div style={{ background: T.surface, padding: '20px 22px', borderTop: `2px solid ${T.gold}` }}>
          <p style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>Coachs au club</p>
          <p style={{ fontFamily: T.display, fontSize: 36, lineHeight: 1, color: T.ink, letterSpacing: '.01em' }}>{total}</p>
        </div>

        <div style={{ background: T.surface, padding: '20px 22px', borderTop: `2px solid ${allPrepared ? T.green : T.gold}` }}>
          <p style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>Préparés</p>
          <p style={{ fontFamily: T.display, fontSize: 36, lineHeight: 1, color: T.ink, letterSpacing: '.01em' }}>
            {prepared_count}<span style={{ fontSize: 18, color: T.muted, marginLeft: 4 }}>/ {total}</span>
          </p>
        </div>

        {!isMobile && (
          <div style={{ background: T.surface, padding: '20px 22px', borderTop: `2px solid ${remaining > 0 ? '#f59e0b' : T.green}` }}>
            <p style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>À préparer</p>
            <p style={{ fontFamily: T.display, fontSize: 36, lineHeight: 1, color: remaining > 0 ? '#b45309' : T.green, letterSpacing: '.01em' }}>{remaining}</p>
          </div>
        )}
      </div>

      {/* ── LISTE DES COACHS ── */}
      {coaches.length === 0 ? (
        <div style={{
          background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid ${T.goldBdr}`,
          padding: '60px 24px', textAlign: 'center',
        }}>
          <Users size={32} color={T.muted} style={{ margin: '0 auto 14px', display: 'block' }} />
          <h3 style={{ fontFamily: T.display, fontSize: 22, textTransform: 'uppercase', color: T.ink, marginBottom: 10 }}>
            Aucun coach au club
          </h3>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.muted, marginBottom: 24, lineHeight: 1.6 }}>
            Invite tes coachs pour qu'ils puissent préparer la saison {targetSeason}.
          </p>
          <Link to="/dashboard/members" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', background: T.gold, color: T.ink,
            fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
            textDecoration: 'none',
          }}>
            Inviter un coach <ArrowRight size={12} />
          </Link>
        </div>
      ) : (
        <div style={{ background: T.surface, border: `1px solid ${T.rule}` }}>
          <div style={{ padding: '16px 22px', borderBottom: `1px solid ${T.rule}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 2, height: 14, background: T.gold, display: 'inline-block', flexShrink: 0 }} />
            <h2 style={{ fontFamily: T.display, fontSize: 13, letterSpacing: '.06em', textTransform: 'uppercase', color: T.ink, margin: 0 }}>
              Coachs du club
            </h2>
          </div>

          <div>
            {coaches.map(coach => {
              const isPrepared = coach.is_prepared
              const isNotified = notifiedIds.has(coach.user_id)
              const isPending = coach.status === 'PENDING_INVITATION'
              const isLoading = notifyingId === coach.user_id

              return (
                <div
                  key={coach.user_id || coach.member_id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '16px 22px', borderBottom: `1px solid ${T.rule}`,
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}
                >
                  {/* Avatar / icône */}
                  <div style={{
                    width: 36, height: 36, flexShrink: 0,
                    background: isPrepared ? 'rgba(34,197,94,0.1)' : isPending ? 'rgba(107,114,128,0.1)' : 'rgba(245,158,11,0.1)',
                    border: `1px solid ${isPrepared ? 'rgba(34,197,94,0.3)' : isPending ? 'rgba(107,114,128,0.3)' : 'rgba(245,158,11,0.3)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {isPrepared ? (
                      <Check size={16} color={T.green} />
                    ) : isPending ? (
                      <Mail size={14} color={T.muted} />
                    ) : (
                      <AlertCircle size={14} color="#b45309" />
                    )}
                  </div>

                  {/* Infos coach */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <p style={{ fontSize: 14, fontWeight: 700, color: T.ink, margin: 0 }}>
                        {coach.name || coach.email}
                      </p>
                      {coach.category && (
                        <span style={{
                          fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em',
                          padding: '2px 8px', background: T.goldBg, color: T.goldD,
                          border: `1px solid ${T.goldBdr}`,
                        }}>
                          {coach.category}
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: T.muted, margin: '4px 0 0 0', fontFamily: T.mono, letterSpacing: '.04em' }}>
                      {isPrepared && 'Saison préparée'}
                      {!isPrepared && !isPending && 'En attente de préparation'}
                      {isPending && 'Invitation en attente d\'acceptation'}
                    </p>
                  </div>

                  {/* Action */}
                  <div style={{ flexShrink: 0, marginLeft: isMobile ? 0 : 'auto' }}>
                    {isPrepared && (
                      <span style={{
                        fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                        color: T.green, padding: '6px 10px',
                        background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
                      }}>
                        ✓ Préparé
                      </span>
                    )}

                    {isPending && (
                      <span style={{
                        fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                        color: T.muted, padding: '6px 10px',
                        background: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.2)',
                      }}>
                        En attente
                      </span>
                    )}

                    {!isPrepared && !isPending && (
                      isNotified ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                          color: T.green, padding: '8px 14px',
                          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
                        }}>
                          <Check size={11} /> Notifié
                        </span>
                      ) : (
                        <button
                          onClick={() => handleNotify(coach.user_id)}
                          disabled={isLoading}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 14px',
                            background: isLoading ? T.rule : T.surface,
                            border: `1px solid ${T.gold}`,
                            color: T.gold,
                            fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 700,
                            cursor: isLoading ? 'wait' : 'pointer',
                            transition: 'all .12s',
                          }}
                          onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.background = T.gold; e.currentTarget.style.color = T.ink } }}
                          onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.background = T.surface; e.currentTarget.style.color = T.gold } }}
                        >
                          <Bell size={11} /> {isLoading ? 'Envoi...' : 'Rappeler'}
                        </button>
                      )
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Footer info */}
      <p style={{ marginTop: 20, fontSize: 12, color: T.muted, textAlign: 'center', lineHeight: 1.6 }}>
        Chaque coach prépare sa saison de son côté. Tu peux les notifier pour leur rappeler.
      </p>
    </div>
  )
}
